<script setup lang="ts">
import { computed } from 'vue';

type Emits = {
  (e: 'click'): void;
  (e: 'click:close'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  icon?: string;
  label?: string;
  size?: string;
  isSelected?: boolean;
  isLoading?: boolean;
  count?: string | number;
  color?: string;
  textColor?: string;
};

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  label: undefined,
  size: undefined,
  isSelected: false,
  isLoading: false,
  count: undefined,
  color: undefined,
  textColor: undefined,
});

const color = computed(() => {
  if (props.color) {
    return props.color;
  }
  return props.isSelected ? 'bg-primary-container' : 'bg-secondary-container';
});
const handleClick = () => {
  if (props.isSelected) {
    emit('click:close');
    return;
  }
  emit('click');
};
</script>

<template>
  <div :class="color" class="rounded btn-bg-height d-flex align-center clickable-container">
    <v-btn
      class="rounded text-none"
      :size="size"
      :color="textColor ?? 'on-surface'"
      :append-icon="isSelected && !isLoading ? 'mdi-close' : undefined"
      :prepend-icon="icon"
      :aria-label="count ? `${label}: ${count}` : label"
      label
      variant="text"
      @click="handleClick"
      :ripple="false"
    >
      <slot>
        <span>
          <span v-if="label">{{ label }}</span>
          <span v-if="count" class="pl-1"> ({{ count }})</span>
          <v-progress-circular
            v-else-if="!count && isLoading"
            color="primary"
            size="18"
            width="2"
            class="ml-1"
            indeterminate
          />
        </span>
      </slot>
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-btn__content) {
  flex-wrap: wrap !important;
  white-space: pre-wrap;
}
:deep(.v-btn__underlay) {
  opacity: 0;
}

/**
* Style div to look and act as a button, to make clickable area bigger while keeping the appearance of a smaller button.
 */
.btn-bg-height {
  height: 36px !important;
}

.clickable-container:hover {
  box-shadow: inset 0 0 0 36px rgba(0, 0, 0, 0.04); /* simulate hover area */
}

.clickable-container:active {
  box-shadow: inset 0 0 0 36px rgba(0, 0, 0, 0.2); /* simulate pressed area */
}

.v-btn:hover {
  --v-hover-opacity: 0;
}

.v-btn {
  --v-activated-opacity: 0;
}
</style>
